import React, { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { find, filter } from "lodash";
import SiteWrapper from "../SiteWrapper";
import ContentGutter from "../ContentGutter";
import {
  dispatchEvent,
  pageView,
  useNextButtonDaLabel
} from "../../utils/analytics";
import replaceImages from "../../utils/replaceImages";
import useAppContext from "../AppContext";
import { useParams } from "react-router-dom";
import { CheckboxGroup } from "@ww-digital/prism";
import ProgressBar from "../ProgressBar";
import SequenceProgressBar from "../SequenceProgressBar";
import MultiChoiceGroups from "../MultiChoiceGroups";
import RecipeResults from "../RecipeResults";
import {
  NO_PROGRESS_BAR,
  MULTISELECT_NONE_OF_THESE,
  IMG_WIDTH_100_PERC,
  MULTI_CHOICE_GROUPS,
  RECIPE_FILTER_RESULTS,
  PHQ9B,
  SUICIDE1A,
  SWITCH_HEADERS,
  DOUBLE_NEXT_BUTTON,
  ANSWER_OPTIONAL,
  PAST_DIAGNOSIS2
} from "../../config/tags";
import t from "../../utils/translate";
import cx from "classnames";
import StickyFooter from "../StickyFooter";
import SequenceFooter from "../SequenceFooter";
import { getWeightFromBmi } from "../../utils/bmi";
import useDelay from "../../utils/useDelay";
import useAwaitStateChange from "../../utils/useAwaitStateChange";
import replaceName from "../../utils/replaceName";
import { FIRST_NAME } from "../Profile/constants";
import AsHtmlWithVideos from "../AsHtmlWithVideo";
import { showEligibilityMessage } from "./utils/showEligibilityMessage";
import Modal from "components/Profile/Modal";
import { hideNewAnswers } from "./utils/hideNewAnswers";

export const isChecked = (id, answers) => {
  if (!answers.length) {
    return false;
  }

  return !!find(answers, answer => answer.id === id);
};

export const deleteAnswerById = (filteredAnswers, answerIdToDelete) => {
  return filter(
    filteredAnswers,
    oldAnswer => oldAnswer.id !== answerIdToDelete
  );
};

export default function Question({
  profile,
  question,
  answer,
  nextQuestion,
  handleAnswer,
  onBack,
  onNext: onNextInit
}) {
  const { newQuiz, assessment, answers: allAnswers } = useAppContext();
  const { questionPath } = useParams();
  const answers = answer?.answers ?? [];
  const isMultiple = question.answers.presentationType === "multipleChoice";
  const isSingle = question.answers.presentationType === "singleChoice";
  const daLabelNextButton = useNextButtonDaLabel(
    question,
    answer,
    nextQuestion
  );
  const scroll = useRef(styles.questionWrap);
  const nextFunctionDelayed = useRef(false);

  const { delay } = useDelay();
  const doubleNextButton = question.tags.includes(DOUBLE_NEXT_BUTTON);
  const answerOptional = question.tags.includes(ANSWER_OPTIONAL);
  const [toggleModal, setToggleModal] = useState(false);

  useEffect(() => {
    pageView(`${question.slug}:question:${question.urlPath}`);
  }, [question.urlPath]);

  useEffect(() => {
    if (isSingle) {
      if (
        newQuiz.isSequence ||
        (newQuiz.isUni && !(newQuiz.isUni3 || newQuiz.isUni4))
      ) {
        handleAnswer([]);
      }
    }
  }, [questionPath]);

  const isPastDiagnosisQuestion = question?.tags?.includes(PAST_DIAGNOSIS2);

  const handleCheckboxChange = (isAdding, selectedAnswer) => {
    let filteredAnswers = [...answers];
    if (isAdding) {
      if (selectedAnswer.tags.includes(MULTISELECT_NONE_OF_THESE)) {
        if (isPastDiagnosisQuestion) {
          dispatchEvent({
            event: "non_click_event",
            event_key: "quiz:comorbidities_none_of_these",
            event_category: "quiz",
            event_action: "comorbidities_none_of_these"
          });
        }
        filteredAnswers = [];
      } else {
        const noneOfTheseAnswer = filteredAnswers.find(a =>
          a.tags.includes(MULTISELECT_NONE_OF_THESE)
        );
        if (noneOfTheseAnswer) {
          filteredAnswers = deleteAnswerById(
            filteredAnswers,
            noneOfTheseAnswer.id
          );
        }
      }
      filteredAnswers.push(selectedAnswer);
    } else {
      filteredAnswers = deleteAnswerById(filteredAnswers, selectedAnswer.id);
    }

    handleAnswer(filteredAnswers);
  };

  const handleOnChange = (e, selectedAnswer) => {
    const isAdding = e.target.checked;

    if (isMultiple) {
      handleCheckboxChange(isAdding, selectedAnswer);

      if (newQuiz.isSequence || newQuiz.eli) {
        const showMessage = showEligibilityMessage(
          profile,
          question,
          assessment?.questions,
          allAnswers,
          isAdding,
          selectedAnswer
        );
        setToggleModal(showMessage);
      }
    } else {
      handleRadioChange(selectedAnswer);
    }
  };

  const onBackQuestion = () => {
    onBack();
    document.getElementsByClassName(scroll.current)[0].scrollTop = 0;
  };

  const isSequenceOrUni =
    newQuiz.isSequence ||
    (newQuiz.isUni && !(newQuiz.isUni3 || newQuiz.isUni4));
  const hideFooter =
    newQuiz.isUni && !(newQuiz.isUni3 || newQuiz.isUni4) && isSingle;

  const onNext = async () => {
    if (isSequenceOrUni && nextFunctionDelayed.current) return;
    if (isSequenceOrUni && isSingle && !doubleNextButton) {
      nextFunctionDelayed.current = true;
      await delay(newQuiz.isSequence ? 700 : 300);
      nextFunctionDelayed.current = false;
    }
    // If this is a question without answers.
    if (
      question.answers.presentationMetadata.length > 0 &&
      !question.answers.presentationMetadata[0].text
    ) {
      handleAnswer([question.answers.presentationMetadata[0]]);
    }
    // If this is a question with optional answer.
    // If they did not select an answer, set the answer to the last option.
    if (
      answerOptional &&
      answers.length === 0 &&
      question?.answers?.presentationMetadata?.length > 0
    ) {
      handleAnswer([
        question.answers.presentationMetadata[
          question.answers.presentationMetadata.length - 1
        ]
      ]);
    }

    onNextInit();
    document.getElementsByClassName(scroll.current)[0].scrollTop = 0;
  };

  const { awaitStateChange } = useAwaitStateChange(
    handleAnswer,
    [answers],
    onNext
  );

  const handleRadioChange = selectedAnswer => {
    if (isSequenceOrUni || doubleNextButton) {
      awaitStateChange([selectedAnswer]);
    } else {
      handleAnswer([selectedAnswer]);
    }
  };

  const imageMargin = "32px auto";
  const imageWidth = !question.tags.includes(IMG_WIDTH_100_PERC)
    ? "auto"
    : undefined;
  const imageDisplay = "block";
  let headerText1 = replaceName(
    replaceImages(
      question.headerText1,
      question.images,
      imageMargin,
      imageWidth,
      imageDisplay
    ),
    profile[FIRST_NAME]
  );
  const headerText2 = replaceImages(
    question.headerText2,
    question.images,
    imageMargin,
    imageWidth,
    imageDisplay
  );
  const footerText1 = replaceImages(
    question.footerText1,
    question.images,
    imageMargin,
    imageWidth,
    imageDisplay
  );

  const switchHeaders = question.tags.includes(SWITCH_HEADERS);

  const noProgressBar = question.tags.includes(NO_PROGRESS_BAR);

  const isMultiChoiceGroupsQuestion =
    question.tags.includes(MULTI_CHOICE_GROUPS);
  const isRecipeFilterResultsPage = question.tags.includes(
    RECIPE_FILTER_RESULTS
  );

  const nextButtonProps = {
    label:
      question.tags.includes(PHQ9B) || question.tags.includes(SUICIDE1A)
        ? t("CONFIRM")
        : null,
    onNext: onNext,
    isDisabled:
      (answers.length === 0 &&
        !!question.answers.presentationMetadata[0].text &&
        !answerOptional) ||
      (isMultiChoiceGroupsQuestion &&
        !answers.find(a =>
          a.tags.includes(question.answers.presentationMetadata[0].tags[0])
        )),
    id: question._id,
    "da-category": "prepaywall_assessment",
    "da-action": "next",
    "da-label": daLabelNextButton
  };

  const backButtonProps = {
    onBack: onBackQuestion,
    "da-category": "quiz",
    "da-action": "back_button",
    "da-label": "quiz:back_button",
    "data-testid": "back-button"
  };

  const progressBar = newQuiz.isSequence ? (
    noProgressBar ? null : (
      <SequenceProgressBar
        question={question}
        backButtonProps={backButtonProps}
      />
    )
  ) : (
    <ProgressBar
      question={question}
      backButtonProps={backButtonProps}
      withProgressBar={!noProgressBar}
    />
  );

  const checkHeaderToken = () => {
    if (headerText1.includes("<weightAt27Bmi>")) {
      const bmi = 27;
      headerText1 = headerText1.replace(
        "<weightAt27Bmi>",
        getWeightFromBmi(profile.metric.height, bmi)
      );
    }
    return headerText1;
  };

  const headerText1Block = () => {
    return (
      <AsHtmlWithVideos
        className={cx(styles.question, {
          [styles.switchedHeaders1]: switchHeaders,
          [styles.headerNoDescription]: newQuiz.isSequence && !headerText2
        })}
        value={checkHeaderToken()}
        interpolations={{ name: profile.firstName }}
      />
    );
  };

  const headerText2Block = () => {
    return (
      headerText2 && (
        <AsHtmlWithVideos
          className={cx(styles.description, {
            [styles.switchedHeaders2]: switchHeaders,
            [styles.doubleNextButtonDescription]: doubleNextButton
          })}
          value={headerText2}
          tagType="div"
          interpolations={{ name: profile.firstName }}
        />
      )
    );
  };

  const allCheckboxAnswers = hideNewAnswers(
    newQuiz.isSequence || newQuiz.eli ? "false" : "true",
    question.answers.presentationMetadata
  );

  return (
    <div
      className={cx(styles.questionWrap, {
        [styles.nonStickyMainWrapperCuisine]: isMultiChoiceGroupsQuestion,
        [styles.nonStickyMainWrapperIphone]: newQuiz.isIphone,
        [styles.nonStickyMainWrapperIphoneCuisine]:
          newQuiz.isIphone && isMultiChoiceGroupsQuestion,
        [styles.nonStickyMainWrapperAndroid]: newQuiz.isAndroid,
        [styles.nonStickyMainWrapperAndroidCuisine]:
          newQuiz.isAndroid && isMultiChoiceGroupsQuestion
      })}
    >
      <div className={styles.screenHeader}>{progressBar}</div>

      <div className={styles.questionContent}>
        <ContentGutter>
          <SiteWrapper size="small" contentWrapper>
            {!switchHeaders ? headerText1Block() : headerText2Block()}
            {!switchHeaders ? headerText2Block() : headerText1Block()}
            {isRecipeFilterResultsPage ? (
              <RecipeResults />
            ) : isMultiChoiceGroupsQuestion ? (
              <MultiChoiceGroups
                question={question}
                answers={answers}
                handleAnswer={handleAnswer}
              />
            ) : (
              allCheckboxAnswers.length > 0 &&
              allCheckboxAnswers[0].text && (
                <div
                  className={cx(styles.options, {
                    [styles.doubleNextButton]: doubleNextButton
                  })}
                >
                  <CheckboxGroup
                    checkboxesData={allCheckboxAnswers.map(optionAnswer => ({
                      label: optionAnswer.text,
                      id: optionAnswer.id,
                      checked: isChecked(optionAnswer.id, answers),
                      attributes: isSingle
                        ? {
                            "da-category": "prepaywall_assessment",
                            "da-action": "next",
                            "da-label": `${question?.headerText1}::${optionAnswer?.text}`
                          }
                        : {}
                    }))}
                    onCheckedChange={(checked, id) => {
                      const answer = allCheckboxAnswers.find(a => a.id === id);

                      handleOnChange({ target: { checked } }, answer);
                    }}
                    checkboxLabelClassName={cx(styles.newQuizQuestionLabel, {
                      [styles.newQuizQuestionLabelMultiple]:
                        newQuiz.isSequence && isMultiple,
                      [styles.newQuizQuestionLabelIsSingle]:
                        newQuiz.isSequence && isSingle
                    })}
                    checkboxLabelSelectedClassName={cx(
                      styles.newQuizQuestionLabelSelected,
                      {
                        [styles.newQuizQuestionLabelSelectedIsSingle]:
                          isSequenceOrUni && isSingle
                      }
                    )}
                    checkboxClassName={{
                      [styles.newQuizQuestionCheckbox]: true,
                      [styles.newQuizQuestionCheckboxMulti]: isMultiple,
                      [styles.newQuizQuestionCheckboxAndroid]: newQuiz.isAndroid
                    }}
                    checkboxSelectedClassName={cx({
                      [styles.checkboxSelected]: isSequenceOrUni && isSingle
                    })}
                    preset="pillLong"
                  />
                </div>
              )
            )}
            {footerText1 && (
              <div className={styles.footer}>
                <AsHtmlWithVideos
                  value={footerText1}
                  interpolations={{ name: profile.firstName }}
                />
              </div>
            )}
          </SiteWrapper>
        </ContentGutter>
      </div>
      {isPastDiagnosisQuestion && (
        <>
          <Modal
            isOpen={toggleModal}
            onOpenChange={() => setToggleModal(!toggleModal)}
            title={t("ELIGIBILITY_NOTICE")}
            description={t("ELIGIBILITY_NOTICE_TEXT")}
            close={t("ELIGIBILITY_NOTICE_CONFIRM")}
          />
        </>
      )}
      {newQuiz.isSequence ? (
        <SequenceFooter
          nextButtonProps={nextButtonProps}
          hideButton={isSingle}
        />
      ) : (
        !hideFooter && <StickyFooter nextButtonProps={nextButtonProps} />
      )}
    </div>
  );
}
